import { supabase } from '../../lib/supabase'
import type { DayEntry } from '../../components/agent/scheduleDay'
import type { PeriodDateSkeleton } from '../../lib/payPeriods'
import { SCHEDULE_ENTRY_COLUMNS, dayFor, type EntryRow } from './approvedScheduleStore'
import type { PersonnelStatus } from './personnelDirectory'

/**
 * docs/design/journey-map.md's "Personnel directory": one person a
 * supervisor can see, read from the real tables. The team is the
 * placement in effect today (employee_assignments), so a transfer moves
 * someone the moment its effective date arrives, on every screen that
 * reads a team from the same place.
 */
export type PersonnelPerson = {
  id: string
  name: string
  identifier: string
  title: string
  team: string
  teamId: string
  fieldOffice: string
  // The team supervisor's own name where it can be read: an account reads
  // only its own profile (profiles_read_own), so another supervisor's own
  // team shows none.
  supervisor: string
  seniorityDate: string
  qualifications: string[]
  status: PersonnelStatus
  currentDetail?: string
  currentDetailEndDate?: string
}

type PlacementRow = {
  employee_id: string
  team_id: string
  title: string
  teams: { name: string; supervisor_id: string | null; field_offices: { name: string } | null } | null
  employees: { display_name: string; employee_identifier: string; seniority_date: string } | null
}

type QualificationRow = {
  employee_id: string
  qualifications: { name: string } | null
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "2019-03-01" as "Mar 2019", from the date's own parts so no timezone can
// move it across a month boundary.
function monthYear(dateIso: string): string {
  const [year, month] = dateIso.split('-').map(Number)
  return `${MONTHS[month - 1]} ${year}`
}

// "2026-09-22" as "Sep 22, 2026", the same way.
export function longDate(dateIso: string): string {
  const [year, month, day] = dateIso.split('-').map(Number)
  return `${MONTHS[month - 1]} ${day}, ${year}`
}

function todayIso(): string {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
}

/**
 * Every employee the signed-in account can see, on the team each is placed
 * on today. Row-level security already limits both tables to the caller's
 * own teams (an administrator's, every team's), so this reads no team
 * filter of its own; a page narrows to one team by its id.
 */
export async function getPersonnel(userId: string, userName: string): Promise<PersonnelPerson[]> {
  const today = todayIso()
  const { data: placements, error } = await supabase
    .from('employee_assignments')
    .select(
      'employee_id, team_id, title, teams(name, supervisor_id, field_offices(name)), employees(display_name, employee_identifier, seniority_date)',
    )
    .eq('active', true)
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
  if (error || !placements) return []

  const rows = placements as unknown as PlacementRow[]
  if (rows.length === 0) return []

  const { data: qualificationRows } = await supabase
    .from('employee_qualifications')
    .select('employee_id, qualifications(name)')
    .in(
      'employee_id',
      rows.map((row) => row.employee_id),
    )
    .lte('effective_from', today)
    .or(`effective_to.is.null,effective_to.gte.${today}`)
  const qualificationsByEmployee = new Map<string, string[]>()
  for (const row of (qualificationRows ?? []) as unknown as QualificationRow[]) {
    if (!row.qualifications) continue
    qualificationsByEmployee.set(row.employee_id, [...(qualificationsByEmployee.get(row.employee_id) ?? []), row.qualifications.name])
  }

  return rows
    .filter((row) => row.employees && row.teams)
    .map((row): PersonnelPerson => ({
      id: row.employee_id,
      name: row.employees!.display_name,
      identifier: row.employees!.employee_identifier,
      title: row.title,
      team: row.teams!.name,
      teamId: row.team_id,
      fieldOffice: row.teams!.field_offices?.name ?? '',
      supervisor: row.teams!.supervisor_id === userId ? userName : '',
      seniorityDate: monthYear(row.employees!.seniority_date),
      qualifications: (qualificationsByEmployee.get(row.employee_id) ?? []).sort(),
      status: 'active',
    }))
    .sort((a, b) => a.name.localeCompare(b.name))
}

export type AssignmentHistoryItem = { effectiveDate: string; change: string }

type HistoryRow = {
  effective_from: string
  reason: string | null
  teams: { name: string; field_offices: { name: string } | null } | null
}

// An employee's own placements, oldest first: the first is where they
// joined, each later one a transfer. The supervisor of the team an employee
// is placed on today reads every placement they have had
// (employee_assignments_read_history).
export async function getAssignmentHistory(employeeId: string): Promise<AssignmentHistoryItem[]> {
  const { data, error } = await supabase
    .from('employee_assignments')
    .select('effective_from, reason, teams(name, field_offices(name))')
    .eq('employee_id', employeeId)
    .eq('active', true)
    .order('effective_from', { ascending: true })
  if (error || !data) return []
  return (data as unknown as HistoryRow[]).map((row, index) => ({
    effectiveDate: longDate(row.effective_from),
    change:
      (index === 0
        ? `Assigned to ${row.teams?.name ?? ''}${row.teams?.field_offices ? ` · ${row.teams.field_offices.name}` : ''}`
        : `Transferred to ${row.teams?.name ?? ''}`) + (row.reason ? `: ${row.reason}` : ''),
  }))
}

// One employee's own schedule across the given dates, an entry for each
// date they have one, in date order. Days with none are left out rather
// than invented.
export async function getEmployeeSchedule(employeeId: string, dates: PeriodDateSkeleton[]): Promise<DayEntry[]> {
  const first = dates[0]?.date
  const last = dates[dates.length - 1]?.date
  if (!first || !last) return []
  const { data, error } = await supabase
    .from('schedule_entries')
    .select(SCHEDULE_ENTRY_COLUMNS)
    .eq('employee_id', employeeId)
    .gte('date', first)
    .lte('date', last)
  if (error || !data) return []
  const byDate = new Map((data as unknown as EntryRow[]).map((row) => [row.date, row]))
  return dates.flatMap((date) => {
    const entry = byDate.get(date.date)
    const day = entry ? dayFor(entry) : null
    return day ? [{ ...date, ...day }] : []
  })
}

export type TransferDestination = { id: string; name: string; openDate: string; closeDate: string | null }

// Every team an employee could move to: teams are readable to any
// signed-in account (teams_read_all), and a supervisor may transfer to a
// team they do not own. Closed teams are left out; a temporary team stays
// until its close date, so the transfer form narrows this to the teams
// open on the chosen effective date.
export async function getTransferDestinations(): Promise<TransferDestination[]> {
  const today = new Date().toISOString().slice(0, 10)
  const { data, error } = await supabase
    .from('teams')
    .select('id, name, open_date, close_date')
    .or(`close_date.is.null,close_date.gte.${today}`)
    .order('name', { ascending: true })
  if (error || !data) return []
  return (data as { id: string; name: string; open_date: string; close_date: string | null }[]).map((team) => ({
    id: team.id,
    name: team.name,
    openDate: team.open_date,
    closeDate: team.close_date,
  }))
}

export type TransferParams = {
  employeeIds: string[]
  toTeamId: string
  effectiveDate: string
  reason?: string
}

// The only path to move an employee between teams: v1-transfer-employees
// checks the caller supervises each one's current team, then closes and
// opens the placements in one transaction. Returns whether it landed, the
// same as every other write in this workspace.
export async function transferEmployees(params: TransferParams): Promise<boolean> {
  const { error } = await supabase.functions.invoke('v1-transfer-employees', { body: params })
  return !error
}
